import type { AxiosInstance } from 'axios'
import { isErrorStatus } from '@audience-republic/audience-manager-shared/api/utils'
import type { CustomApiError } from '@audience-republic/audience-manager-shared/api/types'
import { simpleFilter } from '@audience-republic/audience-manager-shared/utils/api/filterBuilder'
import type { ExportMessageRecipientsParams, FetchMessageRecipientsParams, MessageRecipient } from './types'
import { buildMessageRecipientsRequest, generateStatusFilter, mapSuppressedRecipients } from './utils'
import { getEffectiveFilterEngine } from '@/utils/filterEngine'
import { requestApi } from '@/utils/requestApi'

const controller = new AbortController()
// import { CancelTokenSource } from 'axios';

// let fetchRecipientCountCancelToken: CancelTokenSource | null = null;

export default (axios: AxiosInstance) => ({

  async fetchMessageRecipientCount(
    promoterOid: number,
    messageTaskOid: number,
    searchString: string | null,
    searchStringTargets: string[],
    statuses: string[],
  ): Promise<number> {
    // if (fetchRecipientCountCancelToken) {
    //   fetchRecipientCountCancelToken.cancel('Operation cancelled due to new request');
    // }
    // fetchRecipientCountCancelToken = axios.CancelToken.source();
    // const newCancelTokenSource: CancelTokenSource = fetchRecipientCountCancelToken;

    const uri = `/promoter/${promoterOid}/fan-message`
    // Generate filter query
    let filter = ''
    filter += `taskOid=${messageTaskOid}`
    if (statuses && statuses.length > 1) {
      filter += ` AND (${
        statuses
          .map((item: string) => `status=${item}`).join(' OR ')
      })`
    }
    else if (statuses && statuses.length === 1 && statuses[0] !== undefined && statuses[0] !== null) {
      filter += ` AND status=${statuses[0]}`
    }
    // Filtering with a search string will come later
    if (searchString) {
      // We also allow users to search "status" by using keyword, so don't forget "status" in "searchStringTargets"
      filter += ` AND (${
        searchStringTargets
          .map((target: string) => `${target} ILIKE "%${searchString}%"`).join(' OR ')
      })`
    }

    const { data: { count }, status } = await axios.get(uri, {
      signal: controller.signal,
      // cancelToken: newCancelTokenSource.token,
      params: {
        $top: 0,
        $skip: 0,
        $count: true,
        $filter: filter,
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch Message Recipient Count error',
        message: `Error fetching Message Recipient Count`,
        status,
      }
      throw apiError
    }

    return count
  },

  /**
   * Fetches a page of recipients (fan-messages) of a message. The endpoint and the
   * query are chosen by `buildMessageRecipientsRequest`. Pass a `signal` to cancel
   * the request when it gets superseded.
   */
  async fetchMessageRecipients(
    params: FetchMessageRecipientsParams,
    signal?: AbortSignal,
  ): Promise<MessageRecipient[] | undefined> {
    const { endpointKey, params: query } = buildMessageRecipientsRequest(params)
    const data = await requestApi<unknown, MessageRecipient[]>(endpointKey, { params: query, signal })
    if (!data) return undefined
    return endpointKey === 'fetchSuppressedFanMessages' ? mapSuppressedRecipients(data) : data
  },

  /** Count of rejected SMS fan-messages (not part of the message `statusDetails`). */
  async fetchRejectedRecipientsCount(messageTaskOid: number): Promise<number | null> {
    const data = await requestApi<unknown, { count?: number }>('fetchSmsFanMessages', {
      params: {
        $top: 0,
        $skip: 0,
        $count: true,
        $filter: simpleFilter({ taskOid: messageTaskOid, status: 'rejected' }),
      },
    })
    return data?.count ?? null
  },

  /** Count of recipients matching a clicked-url filter (filter-fan-message). */
  async fetchClickedUrlRecipientCount(
    promoterOid: number,
    {
      messageTaskOid,
      promoterMessageOid,
      clickedUrl,
      searchString = null,
      isSms = false,
    }: {
      messageTaskOid: number
      promoterMessageOid?: number | null
      clickedUrl: string
      searchString?: string | null
      isSms?: boolean
    },
  ): Promise<{ filterFanCount: number }[]> {
    const { data } = await axios.get(`/promoter/${promoterOid}/filter-fan-message`, {
      params: {
        $taskOid: messageTaskOid,
        $count: true,
        $promoterMessageOid: promoterMessageOid,
        $clickedUrl: clickedUrl,
        ...(searchString && { $search: searchString }),
        ...(isSms && { $sms: true }),
        $filterEngine: getEffectiveFilterEngine(),
      },
    })
    return data
  },

  async fetchSuppressedRecipientsCount(
    promoterOid: number,
    messageTaskOid: number,
  ): Promise<number> {
    const { data } = await axios.get(`/promoter/${promoterOid}/fan-message-excluded`, {
      params: {
        $count: true,
        $filter: simpleFilter({ taskOid: messageTaskOid, status: 'suppressed' }),
      },
    })
    return data?.count || 0
  },

  /** Returns the CSV content of the recipients of a message. */
  async exportMessageRecipientsCsv(
    promoterOid: number,
    {
      messageTaskOid,
      select,
      orderBy,
      statuses,
      combineStatusOp,
      excludeRejected = false,
      clickedUrlFilter = null,
      promoterMessageOid = null,
    }: ExportMessageRecipientsParams,
  ): Promise<{ data: string, status: number }> {
    const statusFilter = [excludeRejected ? 'status != rejected' : undefined, generateStatusFilter(statuses, combineStatusOp)]
      .filter(Boolean)
      .join(' AND ') || undefined
    const { data, status } = await axios.get(`/promoter/${promoterOid}/filter-fan-export`, {
      params: {
        $top: 'all',
        $select: select.join(','),
        $orderby: `${orderBy.key} ${orderBy.order}`,
        $messageTaskOid: messageTaskOid,
        $promoterMessageOid: promoterMessageOid,
        ...(statusFilter && { $filter: statusFilter }),
        ...(clickedUrlFilter && { $clickedUrl: clickedUrlFilter[1] }),
        $filterEngine: getEffectiveFilterEngine(),
      },
      headers: { Accept: 'application/json' },
    })
    return { data, status }
  },
})
